import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { base44 } from "api/apiClient";
import Layout from "../components/Layout";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Card, CardContent } from "components/ui/card";
import { Bot, Send, Loader2, TrendingUp, Sparkles, Calendar } from "lucide-react";
import { Badge } from "components/ui/badge";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { format } from "date-fns";

export default function CarloAgent() {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    loadConversations();
  }, []);

  // Load messages when switching to a conversation
  useEffect(() => {
    if (currentConversationId) {
      base44.agents.getConversation(currentConversationId)
        .then((data) => setMessages(data.messages || []))
        .catch((err) => console.error("Error loading messages:", err));
    }
  }, [currentConversationId]);

  const loadConversations = async () => {
    try {
      const convos = await base44.agents.listConversations({
        agent_name: "carlo_forecast",
      });
      setConversations(convos || []);

      if (convos && convos.length > 0 && !currentConversationId) {
        const latestConvo = convos[0];
        setCurrentConversationId(latestConvo.id);
        const detail = await base44.agents.getConversation(latestConvo.id);
        setMessages(detail.messages || []);
      }
    } catch (error) {
      console.error("Error loading conversations:", error);
    }
  };

  const createNewConversation = async () => {
    try {
      const conversation = await base44.agents.createConversation({
        agent_name: "carlo_forecast",
        metadata: {
          name: `Carlo Session - ${format(new Date(), 'dd.MM.yyyy HH:mm')}`,
          description: t("carloAgent.dayForecastPrompt"),
        }
      });
      setCurrentConversationId(conversation.id);
      setMessages([]);
      await loadConversations();
    } catch (error) {
      console.error("Error creating conversation:", error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    setIsLoading(true);
    const userMessage = input;
    setInput("");

    // Show user message optimistically
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

    try {
      let conversationId = currentConversationId;

      if (!conversationId) {
        const conversation = await base44.agents.createConversation({
          agent_name: "carlo_forecast",
          metadata: {
            name: `Carlo Session - ${format(new Date(), 'dd.MM.yyyy HH:mm')}`,
            description: t("carloAgent.dayForecastPrompt"),
          }
        });
        conversationId = conversation.id;
        setCurrentConversationId(conversationId);
        loadConversations();
      }

      const result = await base44.agents.addMessage({ id: conversationId }, {
        role: "user",
        content: userMessage,
      });

      if (result.assistant_message) {
        setMessages(prev => [...prev, result.assistant_message]);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, there was an error processing your message. Please try again.',
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const quickActions = [
    { label: t("carloAgent.dayForecast"), prompt: t("carloAgent.dayForecastPrompt") },
    { label: t("carloAgent.weekForecast"), prompt: t("carloAgent.weekForecastPrompt") },
    { label: t("carloAgent.orderSuggestions"), prompt: t("carloAgent.orderSuggestionsPrompt") },
    { label: t("carloAgent.consumptionAnalysis"), prompt: t("carloAgent.consumptionAnalysisPrompt") },
  ];

  const MessageBubble = ({ message }) => {
    const isUser = message.role === 'user';
    
    return (
      <div className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}>
        {!isUser && (
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center mt-0.5 shadow-md flex-shrink-0">
            <Bot className="w-5 h-5 text-white" />
          </div>
        )}
        <div className={`max-w-[85%] ${isUser && "flex flex-col items-end"}`}>
          {message.content && (
            <div className={`rounded-2xl px-5 py-3 ${
              isUser 
                ? "bg-emerald-700 text-white shadow-md"
                : "bg-white border border-slate-200 shadow-sm"
            }`}>
              {isUser ? (
                <p className="text-sm leading-relaxed">{message.content}</p>
              ) : (
                <ReactMarkdown
                  className="text-sm prose prose-sm prose-slate max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSanitize]}
                  components={{
                    p: ({ children }) => <p className="my-2 leading-relaxed">{children}</p>,
                    ul: ({ children }) => <ul className="my-2 ml-4 list-disc space-y-1">{children}</ul>,
                    ol: ({ children }) => <ol className="my-2 ml-4 list-decimal space-y-1">{children}</ol>,
                    li: ({ children }) => <li className="my-1">{children}</li>,
                    h1: ({ children }) => <h1 className="text-lg font-bold my-3">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-base font-bold my-2">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-sm font-semibold my-2">{children}</h3>,
                    strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
                    code: ({ inline, children }) => (
                      inline ? (
                        <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-mono">
                          {children}
                        </code>
                      ) : (
                        <code className="block bg-slate-900 text-slate-100 rounded-lg p-3 my-2 text-xs font-mono overflow-x-auto">
                          {children}
                        </code>
                      )
                    ),
                    table: ({ children }) => (
                      <div className="my-3 overflow-x-auto rounded-lg border border-slate-200">
                        <table className="min-w-full text-xs">{children}</table>
                      </div>
                    ),
                    thead: ({ children }) => <thead className="bg-slate-50 border-b border-slate-200">{children}</thead>,
                    th: ({ children }) => <th className="px-3 py-2 text-left font-semibold text-slate-700">{children}</th>,
                    td: ({ children }) => <td className="px-3 py-2 border-t border-slate-100">{children}</td>,
                    tr: ({ children }) => <tr className="even:bg-slate-50/50">{children}</tr>,
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              )}
            </div>
          )}
          
          {message.tool_calls && message.tool_calls.length > 0 && (
            <div className="mt-2 space-y-1">
              {message.tool_calls.map((toolCall, idx) => (
                <div key={idx} className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-3 h-3 text-emerald-600" />
                    <span className="font-medium text-slate-700">
                      {toolCall.name?.split('.').pop() || t("carloAgent.action")}
                    </span>
                    {toolCall.status === 'completed' && (
                      <Badge className="ml-auto bg-green-100 text-green-700 border-green-200 text-xs">
                        {t("carloAgent.completed")}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="h-full flex -m-6">
        {/* Conversation Sidebar */}
        <div className="w-80 border-r border-slate-200 bg-white flex flex-col">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl flex items-center justify-center shadow-lg">
              <Bot className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-xl text-slate-900">{t("carloAgent.title")}</h2>
              <p className="text-xs text-slate-500 font-medium">{t("carloAgent.subtitle")}</p>
            </div>
          </div>
          <Button
            onClick={createNewConversation}
            className="w-full bg-emerald-700 hover:bg-emerald-800 shadow-md"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            {t("carloAgent.newAnalysis")}
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-2">
            {t("carloAgent.quickActions")}
          </h3>
          <div className="space-y-2">
            {quickActions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setInput(action.prompt);
                }}
                className="w-full text-left p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors text-sm border border-slate-200"
              >
                <span className="font-medium text-slate-900">{action.label}</span>
              </button>
            ))}
          </div>

          {conversations.length > 0 && (
            <>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-2 mt-6">
                {t("carloAgent.recentConversations")}
              </h3>
              <div className="space-y-2">
                {conversations.slice(0, 5).map((convo) => (
                  <button
                    key={convo.id}
                    onClick={async () => {
                      setCurrentConversationId(convo.id);
                      try {
                        const detail = await base44.agents.getConversation(convo.id);
                        setMessages(detail.messages || []);
                      } catch (err) {
                        console.error("Error loading conversation:", err);
                        setMessages([]);
                      }
                    }}
                    className={`w-full text-left p-3 rounded-xl transition-colors text-sm border ${
                      currentConversationId === convo.id
                        ? "bg-emerald-50 border-emerald-200"
                        : "bg-white border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-3 h-3 text-slate-400" />
                      <span className="text-xs text-slate-500">
                        {format(new Date(convo.created_date), 'dd.MM.yyyy HH:mm')}
                      </span>
                    </div>
                    <p className="font-medium text-slate-900 truncate">
                      {convo.metadata?.name || 'Carlo Session'}
                    </p>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto space-y-6">
            {messages.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                  <Bot className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">
                  {t("carloAgent.hello")}
                </h2>
                <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
                  {t("carloAgent.description")}
                </p>
                <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                  {quickActions.map((action, idx) => (
                    <Card key={idx} className="border-0 shadow-md hover:shadow-xl transition-all cursor-pointer" onClick={() => setInput(action.prompt)}>
                      <CardContent className="p-4">
                        <p className="font-semibold text-slate-900 text-sm">{action.label}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((message, idx) => (
                <MessageBubble key={idx} message={message} />
              ))
            )}
            {isLoading && (
              <div className="flex gap-3 justify-start">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center shadow-md flex-shrink-0">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl px-5 py-3 shadow-sm">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                    <span className="text-sm text-slate-600">{t("carloAgent.analyzing")}</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area */}
        <div className="border-t border-slate-200 bg-white/80 backdrop-blur-sm p-6">
          <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto">
            <div className="flex gap-3">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("carloAgent.placeholder")}
                className="flex-1 bg-white shadow-sm border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                disabled={isLoading}
              />
              <Button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="bg-emerald-700 hover:bg-emerald-800 shadow-md px-6"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
      </div>
    </Layout>
  );
}

